// AudioEmbedBlockEditor — URL-based audio embed
// (SoundCloud / Spotify / Bandcamp / Apple Music / Artlist / other).
//
// Single URL field plus a style switch (standard vs compact player size).
// Preview is best-effort: we build oEmbed-style iframes for the providers
// where the URL pattern is known; otherwise we show only metadata.

import { useMemo } from "react";
import type { AudioEmbedBlock, Block, MediaWidth } from "../../../lib/blocks";
import { detectProvider } from "../../../lib/blocks";
import { t } from "../theme";

export interface AudioEmbedBlockEditorProps {
  block: AudioEmbedBlock;
  onChange: (patch: Partial<Block>) => void;
  isSelected: boolean;
}

const WIDTHS: MediaWidth[] = ["narrow", "regular", "wide", "full"];

// Best-effort iframe builder. Compact -> short height; standard -> normal.
function buildEmbedSrc(url: string, compact: boolean): { src: string; height: number } | null {
  if (!url) return null;
  const provider = detectProvider(url);
  try {
    if (provider === "spotify") {
      // /track/ID, /album/ID, /playlist/ID — passthrough to /embed/ with same shape.
      const u = new URL(url);
      const path = u.pathname.replace(/^\//, "");
      return { src: `https://open.spotify.com/embed/${path}`, height: compact ? 80 : 232 };
    }
    if (provider === "soundcloud") {
      const enc = encodeURIComponent(url);
      return {
        src: `https://w.soundcloud.com/player/?url=${enc}&color=%23b8864a&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=${compact ? "false" : "true"}`,
        height: compact ? 120 : 300,
      };
    }
    if (provider === "bandcamp") {
      // Bandcamp embed is unique per album/track; require the user to
      // paste the embed iframe via Embed block — here just hint.
      return null;
    }
  } catch {
    return null;
  }
  return null;
}

export function AudioEmbedBlockEditor({
  block,
  onChange,
  isSelected,
}: AudioEmbedBlockEditorProps) {
  function setUrl(url: string) {
    const provider = detectProvider(url);
    onChange({ url, provider } as Partial<AudioEmbedBlock>);
  }

  const compact = block.style === "compact";
  const embed = useMemo(() => buildEmbedSrc(block.url, compact), [block.url, compact]);

  return (
    <div>
      {isSelected && (
        <div style={{ display: "flex", gap: 6, marginBottom: 8, fontFamily: t.sans, flexWrap: "wrap" }}>
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ width: w } as Partial<AudioEmbedBlock>)}
              style={pillStyle((block.width || "regular") === w)}
            >
              {w}
            </button>
          ))}
          <div style={{ width: 8 }} />
          {(["standard", "compact"] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onChange({ style: s } as Partial<AudioEmbedBlock>)}
              style={pillStyle((block.style || "standard") === s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <input
        type="url"
        value={block.url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://open.spotify.com/… or https://soundcloud.com/…"
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "8px 10px",
          background: t.bg,
          border: `1px solid ${t.border}`,
          borderRadius: 6,
          fontSize: 13,
          color: t.text,
          outline: "none",
          fontFamily: t.sans,
        }}
      />

      {block.url && (
        <div
          style={{
            marginTop: 8,
            fontSize: 11,
            color: t.text3,
            fontFamily: t.sans,
          }}
        >
          Provider: {block.provider || "other"}
        </div>
      )}

      {embed ? (
        <iframe
          src={embed.src}
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          style={{
            width: "100%",
            height: embed.height,
            border: 0,
            borderRadius: 6,
            marginTop: 10,
            background: "#000",
          }}
        />
      ) : (
        block.url && (
          <div
            style={{
              marginTop: 10,
              padding: 14,
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              color: t.text3,
              fontFamily: t.sans,
              fontSize: 12,
              background: t.bg,
            }}
          >
            Preview not available — will render on the article page if the provider supports it.
          </div>
        )
      )}

      {isSelected && (
        <input
          type="text"
          value={block.caption || ""}
          onChange={(e) =>
            onChange({ caption: e.target.value } as Partial<AudioEmbedBlock>)
          }
          placeholder="Caption (optional)"
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginTop: 8,
            padding: "6px 8px",
            background: t.bg,
            border: `1px solid ${t.border}`,
            borderRadius: 4,
            fontSize: 12,
            color: t.text,
            outline: "none",
            fontFamily: t.sans,
          }}
        />
      )}
    </div>
  );
}

function pillStyle(active: boolean): React.CSSProperties {
  return {
    padding: "3px 8px",
    fontSize: 11,
    borderRadius: 4,
    background: active ? t.accentSoft : "transparent",
    color: active ? t.accent : t.text3,
    border: `1px solid ${active ? t.accentStrong : t.border}`,
    cursor: "pointer",
    textTransform: "capitalize",
  };
}
